import { IAmount } from "./fee";
import { config, SupportedLocales } from "config";

export interface IPluginFee {
    "timeOfCharging": "IMMEDIATELY" | "ACCOUNT_STATEMENT" | "UNKNOWN";
    "periodOfCharging": "MONTHLY" | "NON_RECURRING" | "UNKNOWN";
    "amount": IAmount;
}

// activeInBundle denotes, whether a plugin is active as part of a Bundle. when a plugin is activeInBundle it is inherently also active (both flags are delivered)
// bundleOnly denotes that a plugin can only be activated through a bundle
export type PluginFlag = "active" | "activeInBundle" | "bundleOnly";

export type PluginType = "PLUGIN" | "BUNDLE";

// Many other plugins exist, but we are only interested in PI-VOUCHER for now -> so don't type other productCodes
export type PluginCode = "PI-VOUCHER";

export interface IPlugin {
    "productCode": PluginCode;
    "name": string;
    "type": PluginType;
    "fees": IPluginFee[];
    "standardFees": IPluginFee[];
    "validUntil": Date;
    "dateOfActivation": Date;
    "pluginsInBundle": string[];
    "pluginAvailableInBundles": string[];
    "flags": PluginFlag[];
}

export interface IPluginResponse {
    plugins: IPlugin[];
}

export interface IPluginPurchaseDetails {
    flags: string[];
    id: string;
    status: "DRAFT";
    _links: {
        agreementsList: {
            href: string;
        },
        emailsList?: {
            href: string;
        }
    };
}

export interface IPutPluginPurchaseDetailsPayload {
    id: string;
    status: "COMPLETE";
    contactId: string;
    agreements: {
        code: string;
        text: {
            id: string;
            versionAgreed: string;
        }
    }[];
}

export interface IAgreement {
    code: string;
    flags: ("mandatory" | "optional")[];
    document?: {
        _links: {
            documentDownload?: {
                href: string;
            }
        }
    };
    text: {
        id: string;
    };
}

export interface IPluginAgreementList {
    agreements: IAgreement[];
}

export interface IGetTextResponse {
    bodyI18N: string;
    id: string;
    version: string;
}

export interface IPluginImage {
    "id": number;
    "pluginId": number;
    "uri": string;
    "name": string;
    "language": string;
}

type PluginLanguage = "DE" | "EN";

interface IPluginTranslation {
    "id": number;
    "language": "DE" | "EN";
    "text": string; // html string
    "plainText": string; // plain text string
}

type PluginTextType = string;

export interface IPluginText {
    "id": number;
    "pluginId": number;
    "type": PluginTextType;
    "description": any;
    "translations": IPluginTranslation[];
    "textColor": string; // CSS color string
}

interface IPluginComponent {
    "id": number | null;
    "pluginId": number;
    "text": IPluginText;
    "image": IPluginImage;
    "position": number;
    "key": any;
    "positionImage": string;
    "orientationImage": any;
    "backgroundColor": string; // CSS
    "textColor": string; // CSS
    "backgroundImage": any;
    "pluginTextId": number;
    "pluginImageId": number;
}

export interface IPluginDetails {
    "id": number;
    "externalId": string;
    "keyVisual"?: IPluginImage;
    "listImage"?: IPluginImage;
    "xlImage"?: IPluginImage;
    "detailsImage"?: IPluginImage;
    "featured": boolean;
    "position": number;
    "inUse": boolean;
    "hidden": boolean;
    "type": PluginType;
    "texts": IPluginText[];
    "categories": any[];
    "tags": [];
    "images": IPluginImage[];
    "videos": any[];
    "ratings": any[];
    "comments": any[];
    "components": IPluginComponent[];
    "simage"?: IPluginImage;
    "mimage"?: IPluginImage;
    "limage"?: IPluginImage;
}

export function pluginDetailsGetText(details: IPluginDetails, type: PluginTextType, language: SupportedLocales, html = false) {
    const pluginText = details.texts.find(pt => pt.type === type);
    if (!pluginText) {
        return "";
    }

    let lang = language.toUpperCase();
    if (lang !== "EN" && lang !== "DE") {
        lang = config.DEFAULT_LANGUAGE.toUpperCase();
    }

    const translation = pluginText.translations.find(t => t.language === lang);
    if (!translation) {
        return "";
    }

    return html ? translation.text : translation.plainText;
}

export function pluginIsActive(plugin?: IPlugin) {
    if (!plugin) {
        return false;
    }

    const activeFlag = plugin.flags ? plugin.flags.find(flag => flag === "active") : undefined;
    return !!activeFlag;
}

export function pluginIsProductCodeActive(plugins: IPlugin[], productCode: string) {
    const foundPlugin = plugins.find(p => p.productCode === productCode);
    return pluginIsActive(foundPlugin);
}



// WEBPACK FOOTER //
// ./src/types/plugins.ts